import { useEffect, useMemo, useState } from "react";
import { BotsControl } from "@/components/clicks/BotsControl";
import {
  columnKindOf,
  describeColumnFilter,
  liveColumnFilters,
  mergeColumnFilters,
  parkedColumnFilters,
  parseColumnFilters,
  type ColumnFilter,
} from "@/components/clicks/clicksGridEngine";
import { getDimensionLabel, metricLabel } from "@/components/clicks/metricDisplay";
import { filterKeyForGroupBy } from "@/config/dimensions";
import { FILTER_FIELD_GROUPS, filterFieldLabel, markField, type FilterField } from "@/config/filterFields";
import type { ClicksParams } from "@/hooks/useClicksData";
import { useClicksMarks } from "@/hooks/useClicksMarks";
import { useUrlFilters } from "@/hooks/useUrlFilters";
import { BOT_FILTER_KEY } from "@/lib/bot-presets";
import {
  activeFieldKeys,
  describeFieldFilter,
  fieldFilterOf,
  isMarkKey,
  markFieldOf,
  markFilterKey,
  withFieldFilter,
  withoutFieldFilters,
  type FieldFilter,
} from "@/lib/filter-model";
import { stateFromRows, type BuilderRow } from "@/lib/filter-builder";
import { readPinned, readRecent, togglePinned, withRecent, writePinned, writeRecent } from "@/lib/filter-prefs";
import { parseUrlActiveFilters, serializeUrlActiveFilters, type UrlActiveFilters } from "@/lib/url-active-filters";
import { AddFilterMenu } from "./AddFilterMenu";
import { AiFilterButton } from "./AiFilterButton";
import { ConditionChip, InactiveConditionChip } from "./ConditionChip";
import { FilterBuilder, type ColumnOption } from "./FilterBuilder";
import { FilterChip } from "./FilterChip";

const FIELDS = new Map(FILTER_FIELD_GROUPS.flatMap((g) => g.fields).map((f) => [f.key, f]));
const withoutBots = (f: UrlActiveFilters): UrlActiveFilters =>
  Object.fromEntries(Object.entries(f).filter(([k]) => k !== BOT_FILTER_KEY));

/**
 * Focus `selector` once it renders (a chip appears after the router commits),
 * but only if focus is lost - a click on another control keeps its focus.
 */
const focusSoon = (selector: string) => {
  let tries = 0;
  const tick = () => {
    const active = document.activeElement;
    if (active && active !== document.body) return;
    const el = document.querySelector<HTMLElement>(selector);
    if (el) el.focus();
    else if (tries++ < 60) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
};
const ADD_FILTER = "[data-add-filter]";
const chipSelector = (key: string) => `[data-chip-key="${CSS.escape(key)}"]`;

/**
 * The filters of /clicks, in one row above the report (docs/clicks-filters-variants.html,
 * variant 1 + quick slots; docs/clicks-filters-more-variants.html, 30.10.2026):
 * "Filters · N" (every filter as a list), the Bots preset, a chip per filtered
 * field, a chip per column condition (table rows only), "Add filter" with the
 * recent sets, the user's own quick slots, and the AI field (placeholder).
 * Fields AND together, a field's values OR. Everything lives in the URL -
 * `filters` and `where` - so links, saved views and the menus keep working.
 */
export function FilterBar({ params, columnFilters = {}, columnContext, columns = [] }: {
  params: ClicksParams;
  /** The column conditions that apply to the grid now (liveColumnFilters). */
  columnFilters?: Record<string, ColumnFilter>;
  /** What makes a stored condition apply now - the page's, so both read it alike. */
  columnContext?: { dims: string[]; columns: Set<string> };
  /** The table's columns a condition can be put on. */
  columns?: ColumnOption[];
}) {
  const { filters, where, whereParam, latest, update } = useUrlFilters();
  const dims = params.groupDims?.length ? params.groupDims : [params.groupBy];
  const ctx = columnContext ?? { dims, columns: new Set(columns.map((c) => c.id)) };
  const markDim = dims.length === 1 ? filterKeyForGroupBy(dims[0]) : undefined;
  const { marks, available: marksOn } = useClicksMarks(markDim);
  const markCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const m of marks?.values() ?? []) c[m.mark] = (c[m.mark] ?? 0) + 1;
    return c;
  }, [marks]);

  const fieldOf = (key: string): FilterField =>
    isMarkKey(key)
      ? markField(markFieldOf(key), markFieldOf(key) === markDim ? markCounts : undefined)
      : (FIELDS.get(key) ?? { key, label: filterFieldLabel(key) });
  const columnLabel = (id: string): string =>
    columns.find((c) => c.id === id)?.label ??
    (id.startsWith("dimension:") ? id.slice("dimension:".length).split(",").map(getDimensionLabel).join(" ▸ ") : metricLabel(id));
  const columnKind = (id: string) => columns.find((c) => c.id === id)?.kind ?? columnKindOf(id);

  const active = activeFieldKeys(filters);
  const conditions = Object.entries(columnFilters);
  const parked = parkedColumnFilters(where, ctx);
  const whyParked = (p: (typeof parked)[number]): string =>
    p.reason === "hidden"
      ? "its column is hidden - show it in Columns to apply it"
      : p.key.startsWith("dimension:")
        ? `it applies when grouped by ${columnLabel(p.key)}`
        : "this grouping has no values for its column";
  const [pending, setPending] = useState<string | null>(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  // "+ Filter" on a field already in the bar opens its chip (it used to just close).
  const [reopen, setReopen] = useState<{ key: string; n: number } | null>(null);
  const pick = (key: string) =>
    active.includes(key) ? setReopen((r) => ({ key, n: (r?.n ?? 0) + 1 })) : setPending(key);
  const [pinned, setPinned] = useState(readPinned);
  const togglePin = (key: string) =>
    setPinned((cur) => {
      const next = togglePinned(cur, key);
      writePinned(next);
      return next;
    });

  // A filter set is remembered once it has stood for a moment - not every step
  // of putting it together.
  const [recent, setRecent] = useState(readRecent);
  const nowFilters = serializeUrlActiveFilters(withoutBots(filters)) ?? "";
  const nowWhere = whereParam ?? "";
  useEffect(() => {
    if (!nowFilters && !nowWhere) return;
    const t = setTimeout(
      () =>
        setRecent((cur) => {
          const next = withRecent(cur, { filters: nowFilters, where: nowWhere });
          writeRecent(next);
          return next;
        }),
      1500,
    );
    return () => clearTimeout(t);
  }, [nowFilters, nowWhere]);

  const describeSet = (setFilters: string, setWhere: string): string => {
    const f = parseUrlActiveFilters(setFilters);
    const parts = activeFieldKeys(f).map((k) => {
      const field = fieldOf(k);
      return describeFieldFilter(field.label, fieldFilterOf(f, k)!, field.valueLabel);
    });
    for (const [id, c] of Object.entries(parseColumnFilters(setWhere))) parts.push(`${columnLabel(id)} ${describeColumnFilter(c)}`);
    return parts.join(" · ");
  };
  const recentItems = recent
    .filter((e) => e.filters !== nowFilters || e.where !== nowWhere)
    .map((e) => ({
      label: describeSet(e.filters, e.where),
      // The bot preset stays as it is: it is a control of its own - even if a
      // stored set holds one (an older build, edited storage).
      onPick: () =>
        update({
          filters: (cur) => ({ ...withoutFieldFilters(cur), ...withoutBots(parseUrlActiveFilters(e.filters)) }),
          where: () => parseColumnFilters(e.where),
        }),
    }))
    .filter((r) => r.label);

  const applyField = (key: string, f: FieldFilter | null) => update({ filters: (cur) => withFieldFilter(cur, key, f) });
  const setCondition = (id: string, next: ColumnFilter | null) => {
    const live = { ...columnFilters };
    if (next) live[id] = next;
    else delete live[id];
    update({ where: (cur) => mergeColumnFilters(cur, live, ctx) });
  };
  const markGroups = marksOn && markDim ? [{ title: "Marks", fields: [fieldOf(markFilterKey(markDim))] }] : [];
  const slots = pinned.filter((k) => !active.includes(k) && k !== pending);

  return (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filters">
      <FilterBuilder
        open={builderOpen}
        onOpenChange={setBuilderOpen}
        count={active.length + conditions.length}
        readCurrent={() => {
          const l = latest();
          return { filters: l.filters, columnFilters: liveColumnFilters(l.where, ctx) };
        }}
        columns={columns}
        fieldOf={fieldOf}
        extraGroups={markGroups}
        onApply={(rows: BuilderRow[]) =>
          update({
            filters: (cur) => stateFromRows(rows, cur, columnKind).filters,
            where: (cur) => mergeColumnFilters(cur, stateFromRows(rows, {}, columnKind).columns, ctx),
          })
        }
      />
      <BotsControl params={params} />
      {active.map((key) => (
        <FilterChip
          key={reopen?.key === key ? `${key}#${reopen.n}` : key}
          defaultOpen={reopen?.key === key}
          field={fieldOf(key)}
          filter={fieldFilterOf(filters, key)}
          onApply={(f) => {
            applyField(key, f);
            if (!f) focusSoon(ADD_FILTER);
          }}
        />
      ))}
      {conditions.map(([id, f]) => (
        <ConditionChip key={`c:${id}`} label={columnLabel(id)} kind={columnKind(id)} filter={f} onChange={(next) => setCondition(id, next)} />
      ))}
      {parked.map((p) => (
        <InactiveConditionChip
          key={`p:${p.key}`}
          label={columnLabel(p.key)}
          filter={p.filter}
          why={whyParked(p)}
          onRemove={() =>
            update({ where: (cur) => Object.fromEntries(Object.entries(cur).filter(([k]) => k !== p.key)) })
          }
        />
      ))}
      {pending && !active.includes(pending) ? (
        <FilterChip
          key={`pending:${pending}`}
          field={fieldOf(pending)}
          filter={null}
          defaultOpen
          onApply={(f) => {
            setPending(null);
            applyField(pending, f);
            focusSoon(f ? chipSelector(pending) : ADD_FILTER);
          }}
          onDismiss={() => {
            setPending(null);
            focusSoon(ADD_FILTER);
          }}
        />
      ) : null}
      {/* Before the quick slots, the one filled control of the bar (owner, 03.11.2026). */}
      <AddFilterMenu
        active={[...active, ...(pending ? [pending] : [])]}
        onPick={pick}
        extraGroups={markGroups}
        pinned={pinned}
        onTogglePin={togglePin}
        recent={recentItems}
        filterCount={active.length + conditions.length}
        onOpenBuilder={() => setBuilderOpen(true)}
      />
      {slots.map((key) => (
        <FilterChip key={`slot:${key}`} field={fieldOf(key)} filter={null} onApply={(f) => applyField(key, f)} />
      ))}
      <AiFilterButton />
      {active.length || conditions.length || parked.length ? (
        <button
          type="button"
          onClick={() => {
            update({ filters: withoutFieldFilters, where: () => ({}) });
            focusSoon(ADD_FILTER);
          }}
          className="ml-auto cursor-pointer text-xs text-muted-foreground hover:text-foreground"
        >
          Clear all
        </button>
      ) : null}
    </div>
  );
}
